﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { EditType, ModalMessageType } from '../models/enums';
import { RawMaterialAnalyticalPlan } from '../models/RawMaterials/raw-material-analytical-plan';
import MethodLookupComponent from './Methods/MethodLookupComponent';
import { DataTemplates } from '../templates/data-templates';
import { AnalyticalPlanUpdateRequest } from '../models/analytical-plan-update-request';
import { LookupItem } from '../models/lookup-item';
import { AnalyticalPlansLookupData } from '../models/analytical-plans-lookup-data';
//import { AnalyticalPlanEditObject } from '../models/analytical-plan-edit-object';
import { ModalMetadata } from '../models/modal-metadata';
import GlobalDialog from './Modal/GlobalDialog';
import { ToastProps } from '../models/toast-props';
import { Method } from '../models/Methods/method';
import { FieldUpdate } from '../models/field-update';

import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import SelectBox from 'devextreme-react/select-box';
import List from 'devextreme-react/list';
import Button from 'devextreme-react/button';
import Toast from 'devextreme-react/toast';

import {
    Validator,
    StringLengthRule
} from 'devextreme-react/validator';

import lodash from 'lodash';

export interface Props {
    rawMaterialBatchId: string;
    analyticalPlanId: string;
    lookupData?: AnalyticalPlansLookupData;
    //analyticalPlanEditObject: AnalyticalPlanEditObject;
    onClose?: () => void;
}

export default function RawMaterialAnalyticalPlanEditor(props: Props) {
    const [analyticalPlan, setAnalyticalPlan] =
        useState<RawMaterialAnalyticalPlan>();

    //const [originalAnalyticalPlan, setOriginalAnalyticalPlan] =
      //  useState<RawMaterialAnalyticalPlan>();

    const [analysisTypes, setAnalysisTypes] =
        useState<LookupItem[]>();

    const [basisTypes, setBasisTypes] =
        useState<LookupItem[]>();

    const [technologies, setTechnologies] =
        useState<LookupItem[]>();

    const [methodLookupComponentVisible, setMethodLookupComponentVisible] =
        useState<boolean>();

    const [analyticalPlanAnalysisTypesSelectedItemKeys, setAnalyticalPlanAnalysisTypesSelectedItemKeys] =
        useState<number[]>();

    const [analyticalPlanBasisTypesSelectedItemKeys, setAnalyticalPlanBasisTypesSelectedItemKeys] =
        useState<number[]>();

    const [areFieldChanges, setAreFieldChanges] =
        useState<boolean>(false)

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()
        
    useEffect(() => {
        setInitValues();
    }, [analyticalPlan]);

    useEffect(() => {
        getRawMaterialAnalyticalPlan();
    }, [props.analyticalPlanId, props.lookupData]);

    return (
        <div>
            <div style={{ margin: 'auto' }}>
                <div className="pw-comp-row pw-margin-top-05">
                    <div className="title-bar-spacer" />
                    <div className="save-toolbar pw-margin-right-05">
                        <Button id="saveButton" text="Save" icon="save" type="success"
                            className="pw-button"
                            hint="save changes"
                            height={'1.8rem'}
                            onClick={onSaveClick} />

                        <Button id="closeButton" text="Close" icon="close" type="normal"
                            className="pw-button"
                            hint="close dialog"
                            height={'1.8rem'}
                            onClick={onCloseClick} />
                    </div>
                </div>
            </div>

            <div>
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Technology</div>
                        <SelectBox id="analyticalTechnologyId" className="selectbox"
                            placeholder='NA'
                            itemRender={DataTemplates.ProductUomItemRender}
                            displayExpr={'name'} valueExpr={'id'}
                            dataSource={technologies ? technologies : []}
                            onValueChanged={onTechnologyChanged}
                            value={analyticalPlan ? analyticalPlan.analyticalTechnologyId : null} />
                    </div>

                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Specification</div>
                        <NumberBox id="specification" className="textbox"
                            onValueChanged={onAnalyticalPlanValueChanged}
                            placeholder='NA'
                            value={analyticalPlan ? analyticalPlan.specification : undefined} />
                    </div>

                    <div className="pw-form-unit pw-comp-15">
                        <div className="pw-form-unit-label">Method Name</div>
                        <TextBox id="methodName" className="textbox"
                            onValueChanged={onAnalyticalPlanValueChanged}
                            placeholder='NA'
                            value={analyticalPlan ? analyticalPlan.methodName : undefined} >
                            <Validator>
                                <StringLengthRule message="Catalog Number must be less than 60 characters in length." max={60} />
                            </Validator>
                        </TextBox>
                    </div>

                    <div className="pw-form-unit pw-comp-10">
                        <div className="pw-form-unit-label">Version #</div>
                        <NumberBox id="methodVersionNumber" className="textbox"
                            disabled={true}
                            value={analyticalPlan ? analyticalPlan.methodVersionNumber : undefined} />
                    </div>

                    <div className="pw-row-button-08">
                        <Button id="lookupMethodButton" text="" icon="edit" type="success"
                            className="pw-button"
                            hint="lookup method"
                            height='1.6rem'
                            onClick={onMethodLookupClick} />
                    </div>
                </div>
            </div>

            {methodLookupComponentVisible &&
                <div>
                    <MethodLookupComponent gridHeight="12rem" showTitle={true}
                        onApplySelection={onApplyMethodApplySelectionClick}
                        onCancel={onHideMethodLookupClick} />
                </div>
            }

            <div className="pw-form-unit">
                <div className="pw-form-unit-label">Description</div>
                <TextBox id="description" className="textbox"
                    onValueChanged={onAnalyticalPlanValueChanged}
                    placeholder='NA'
                    value={analyticalPlan ? analyticalPlan.description : undefined} >
                    <Validator>
                        <StringLengthRule message="Description must be less than 200 characters in length." max={200} />
                    </Validator>
                </TextBox>
            </div>

            <div className="flex-col-stretch pw-margin-top-05 pw-margin-bottom-05 min-height-13">
                <div className="flex-child-equal-width pw-list-border pw-margin-lr-05">
                    <div className="lq-header pw-bg-purple">
                        <div className="lq-header-title">Analysis Types</div>
                    </div>

                    <List height='10rem'
                        items={analysisTypes ? analysisTypes : []}
                        keyExpr="id"
                        itemRender={DataTemplates.EhsTemplate}
                        showSelectionControls={true}
                        selectionMode="multiple"
                        onOptionChanged={analyticalAnalysisTypesChanged}
                        selectedItemKeys={analyticalPlanAnalysisTypesSelectedItemKeys ?
                            analyticalPlanAnalysisTypesSelectedItemKeys : []} />
                </div>

                <div className="flex-child-equal-width pw-list-border">
                    <div className="lq-header pw-bg-cyan">
                        <div className="lq-header-title">Basis Types</div>
                    </div>

                    <List items={basisTypes ? basisTypes : []}
                        keyExpr="id"
                        itemRender={DataTemplates.EhsTemplate}
                        showSelectionControls={true}
                        selectionMode="multiple"
                        onOptionChanged={analyticalBasisTypesChanged}
                        selectedItemKeys={analyticalPlanBasisTypesSelectedItemKeys ?
                            analyticalPlanBasisTypesSelectedItemKeys : []} />
                </div>
            </div>

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    async function onApplyMethodApplySelectionClick(method: Method) {
        console.log("onApplyMethodApplySelectionClick:");
        console.log(method);

        //is rawMaterialBatchId right??
        if (method && method.methodId && props.analyticalPlanId) {

            var fieldUpdate: FieldUpdate = {
                rowId: props.analyticalPlanId,
                field: "methodId",
                value: method.methodId
            };

            await updateAnalyticalPlanField(fieldUpdate)
                .then(() => {
                    showToast("Method ID Updated Successfully", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Method ID");
                });

        }

        setMethodLookupComponentVisible(false);
    }

    function onSaveClick() {
        saveData();
    }

    function onCloseClick() {
        if (props.onClose)
            props.onClose();
    }

    function onTechnologyChanged(e: any) {
        setAreFieldChanges(true);
        setAnalyticalPlan({ ...analyticalPlan, analyticalTechnologyId: e.value });
    }

    function onAnalyticalPlanValueChanged(e: any) {

        const caller = e.element.id as string;

        setAreFieldChanges(true);

        setAnalyticalPlan({ ...analyticalPlan, [caller]: e.value });
    }

    function analyticalAnalysisTypesChanged(e: any) {
        if (e.name === 'selectedItemKeys' && analyticalPlan && analyticalPlan.analyticalPlanId) {
            setAnalyticalPlanAnalysisTypesSelectedItemKeys(e.value);
        }
    }

    function analyticalBasisTypesChanged(e: any) {
        if (e.name === 'selectedItemKeys' && analyticalPlan && analyticalPlan.analyticalPlanId) {
            setAnalyticalPlanBasisTypesSelectedItemKeys(e.value);
        }
    }



    function setInitValues() {

        console.log("setInitValues():");

        console.log("props.lookupData:")
        console.log(props.lookupData)
        console.log("analyticalPlan:");
        console.log(analyticalPlan);





        //const ap = props.analyticalPlanEditObject.analyticalPlan;
        //setAnalyticalPlan(ap);
        //setOriginalAnalyticalPlan({ ...ap });

        if (!props.lookupData || !analyticalPlan)
            return;

        setTechnologies(props.lookupData.technologyTypes);

        const lookupData = props.lookupData;

        const analysisTypesGroup = lookupData.technologyAnalysisTypes.find(t => t.id === analyticalPlan.analyticalTechnologyId);
        const basisTypesGroup = lookupData.technologyBasisTypes.find(t => t.id === analyticalPlan.analyticalTechnologyId);

        if (analysisTypesGroup && analysisTypesGroup.lookupItems)
            setAnalysisTypes(analysisTypesGroup.lookupItems);

        if (basisTypesGroup && basisTypesGroup.lookupItems)
            setBasisTypes(basisTypesGroup.lookupItems);

        let selectedAnalysisTypesKeys = analyticalPlan.analysisTypes.map(a => a.id);
        let selectedBasisTypesKeys = analyticalPlan.basisTypes.map(b => b.id);

        setAnalyticalPlanAnalysisTypesSelectedItemKeys(selectedAnalysisTypesKeys);
        setAnalyticalPlanBasisTypesSelectedItemKeys(selectedBasisTypesKeys);
    }


    /*
    function setInitValues() {
        if (!props.analyticalPlanEditObject)
            return;

        const ap = props.analyticalPlanEditObject.analyticalPlan as RawMaterialAnalyticalPlan;

        setAnalyticalPlan(ap);
        setOriginalAnalyticalPlan({ ...ap });

        setTechnologies(props.analyticalPlanEditObject.lookupData.technologyTypes);

        const lookupData = props.analyticalPlanEditObject.lookupData;

        const analysisTypesGroup = lookupData.technologyAnalysisTypes.find(t => t.id === ap.analyticalTechnologyId);
        const basisTypesGroup = lookupData.technologyBasisTypes.find(t => t.id === ap.analyticalTechnologyId);

        if (analysisTypesGroup && analysisTypesGroup.lookupItems)
            setAnalysisTypes(analysisTypesGroup.lookupItems);

        if (basisTypesGroup && basisTypesGroup.lookupItems)
            setBasisTypes(basisTypesGroup.lookupItems);

        let selectedAnalysisTypesKeys = ap.analysisTypes.map(a => a.id);
        let selectedBasisTypesKeys = ap.basisTypes.map(b => b.id);

        setAnalyticalPlanAnalysisTypesSelectedItemKeys(selectedAnalysisTypesKeys);
        setAnalyticalPlanBasisTypesSelectedItemKeys(selectedBasisTypesKeys);
    }
    */




    async function saveData() {
        if (!analyticalPlan)
            return;

        let areChanges = false;
        let request: AnalyticalPlanUpdateRequest = {
            technologyId: analyticalPlan.analyticalTechnologyId
        };

        //console.log("analyticalPlan:");
        //console.log(analyticalPlan);

        let modifiedAnalyticalPlan = { ...analyticalPlan };

        modifiedAnalyticalPlan.analysisTypes = [];
        modifiedAnalyticalPlan.basisTypes = [];

        for (let apKey of analyticalPlanAnalysisTypesSelectedItemKeys) {
            const matchingAnalysisType = analysisTypes.find(at => at.id === apKey);

            if (matchingAnalysisType)
                modifiedAnalyticalPlan.analysisTypes.push(matchingAnalysisType);
        }

        for (let apKey of analyticalPlanBasisTypesSelectedItemKeys) {
            const matchingBasisType = basisTypes.find(at => at.id === apKey);

            if (matchingBasisType)
                modifiedAnalyticalPlan.basisTypes.push(matchingBasisType);
        }

        request.analyticalPlan = modifiedAnalyticalPlan;


        if (areFieldChanges)
            areChanges = true;


        let analysisTypesDiff = lodash.xorBy(analyticalPlan.analysisTypes, modifiedAnalyticalPlan.analysisTypes, 'id');

        if (analysisTypesDiff && analysisTypesDiff.length > 0) {
            areChanges = true;
            request.analysisTypes = modifiedAnalyticalPlan.analysisTypes;
        }

        let basisTypesDiff = lodash.xorBy(analyticalPlan.basisTypes, modifiedAnalyticalPlan.basisTypes, 'id');

        if (basisTypesDiff && basisTypesDiff.length > 0) {
            areChanges = true;
            request.basisTypes = modifiedAnalyticalPlan.basisTypes;
        }

        console.log("the request:");
        console.log(request);
        console.log(areChanges);

        if (areChanges) {
            await updateAnalyticalPlan(request)
                .then(() => {
                    showToast("Analytical Plan Updated Successfully", "success");
                    setAreFieldChanges(false);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Analytical Plan");
                });
        }


    }


    /*
    async function saveData() {
        let areChanges = false;
       
        let request = { ...analyticalPlan };

        request.analysisTypes = [];
        request.basisTypes = [];

        for (let apKey of analyticalPlanAnalysisTypesSelectedItemKeys) {
            const matchingAnalysisType = analysisTypes.find(at => at.id === apKey);

            if (matchingAnalysisType)
                request.analysisTypes.push(matchingAnalysisType);
        }

        for (let apKey of analyticalPlanBasisTypesSelectedItemKeys) {
            const matchingBasisType = basisTypes.find(at => at.id === apKey);

            if (matchingBasisType)
                request.basisTypes.push(matchingBasisType);
        }

        //request.analyticalPlan = modifiedAnalyticalPlan;

        //compare analytical plan fields
        if (analyticalPlan.description !== originalAnalyticalPlan.description ||
            //analyticalPlan.specification !== originalAnalyticalPlan.specification ||
            analyticalPlan.analyticalTechnologyId !== originalAnalyticalPlan.analyticalTechnologyId ||
            analyticalPlan.methodName !== originalAnalyticalPlan.methodName) {

            areChanges = true;
            request.editType = EditType.Update;
        }

        let analysisTypesDiff = lodash.xorBy(analyticalPlan.analysisTypes, request.analysisTypes, 'id');

        if (analysisTypesDiff && analysisTypesDiff.length > 0) {
            areChanges = true;
            request.analysisTypes = request.analysisTypes;
        }

        let basisTypesDiff = lodash.xorBy(analyticalPlan.basisTypes, request.basisTypes, 'id');

        if (basisTypesDiff && basisTypesDiff.length > 0) {
            areChanges = true;
            request.basisTypes = request.basisTypes;
        }

        //console.log("the request:");
        //console.log(request);
        //console.log(areChanges);

        if (areChanges) {
            await updateAnalyticalPlan(request)
                .then(() => {
                    showToast("Analytical Plan Updated Successfully", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Analytical Plan");
                });
        }
    }
    */

    async function getRawMaterialAnalyticalPlan() {

        console.log("getRawMaterialAnalyticalPlan():");
        console.log(props.analyticalPlanId)


        if (props.analyticalPlanId) {
            await CrudFunctions.getData(`rawmaterials/analyticalplan/${props.analyticalPlanId}`)
                .then((data) => {
                    setAnalyticalPlan(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Analytical Plan");
                });
        }
    }


    async function updateAnalyticalPlan(request: AnalyticalPlanUpdateRequest) {
        return CrudFunctions.putData("rawmaterials/analyticalplan", request);
    }

    async function updateAnalyticalPlanField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("rawmaterials/analyticalplanfield", fieldUpdate);
    }

    function onMethodLookupClick() {
        setMethodLookupComponentVisible(true);
    }

    function onHideMethodLookupClick() {
        setMethodLookupComponentVisible(false);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    //other

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}